/**
 * The student's category.
 *
 * Ported from the app's `category_screen.dart`.
 *
 * *** WITHOUT THIS, WEB BOOKING IS A DEAD END ***
 * Every discovery endpoint is scoped to the category on the student's
 * profile: `/mentors` raises "Choose a category first" when there is none.
 * The booking funnel showed that sentence and offered nothing to act on, so
 * a student who signed up on the web could not book at all (found
 * 2026-11-02).
 *
 * *** CHANGING IT KEEPS EVERY BOOKING ***
 * The server re-scopes discovery and leaves existing sessions alone — they
 * are commitments, not preferences. Said on screen, because a student
 * switching category would reasonably fear losing what they have booked.
 */

import { Card } from '@/components/ui/Page'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import {
  useMyStudentProfile,
  usePublicCategories,
  useSelectCategory,
} from '@/lib/queries'
import type { PublicCategory } from '@/types/api'
import styles from './ProfilePage.module.css'

export function CategoryPicker() {
  const toast = useToast()
  const profile = useMyStudentProfile()
  const categories = usePublicCategories()
  const select = useSelectCategory()

  const chosen = profile.data?.category_id ?? null
  const list = categories.data?.categories ?? []

  /*
    *** AN EMPTY LIST IS A CLAIM ***
    React Query serves a cached list instantly on a revisit, so `isLoading`
    alone would announce "no categories" from data that has not answered.
  */
  const settled = categories.isSuccess && !categories.isFetching

  const pick = async (category: PublicCategory) => {
    if (category.category_id === chosen) return
    try {
      await select.mutateAsync(category.category_id)
      toast.success(`You are now in ${category.name}.`)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not change your category.',
      )
    }
  }

  return (
    <Card>
      <div className={styles.field}>
        <span className={styles.labelRow}>
          {/*
            *** "CATEGORY", NEVER "SUBJECT" ***
            A standing rule in the UI brief. The app's own screen says
            "Choose your category", and the admin screen that defines these
            is Categories — three names for one thing is how a student and
            the person they phone for help stop understanding each other.
          */}
          <span className={styles.label}>Your category</span>
          {/*
            *** THE UNSET STATE IS THE IMPORTANT ONE ***
            It is why a student cannot book, and nothing else on the portal
            says so.
          */}
          {!chosen && settled && (
            <Pill tone="warning" subtle>
              Not chosen
            </Pill>
          )}
        </span>

        <p className={styles.hint}>
          {chosen
            ? 'Mentors and session types are shown for this category. Changing it keeps every session you have already booked.'
            : 'Choose one to see mentors and book a session.'}
        </p>

        {categories.isLoading && (
          <p className={styles.hint}>Loading categories…</p>
        )}

        {categories.error && (
          <p className={styles.error}>
            {categories.error instanceof ApiError
              ? categories.error.message
              : 'Could not load categories.'}
          </p>
        )}

        {settled && list.length === 0 && (
          <p className={styles.hint}>
            Your institute has not set up any categories yet. Ask them.
          </p>
        )}

        {list.length > 0 && (
          <div className={styles.categoryGrid}>
            {list.map((c) => (
              <button
                key={c.category_id}
                type="button"
                className={`${styles.category} ${
                  c.category_id === chosen ? styles.categoryOn : ''
                }`}
                onClick={() => pick(c)}
                disabled={select.isPending}
                aria-pressed={c.category_id === chosen}
              >
                <span className={styles.categoryName}>{c.name}</span>
                {c.description && (
                  <span className={styles.categoryDesc}>{c.description}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
